import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, FileText, Download, Upload, Trash2, Loader2, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../../../../components/ui/custom-dropdown-menu';
import { AllProjectSpecifications } from './AllProjectSpecifications';
import type { DashboardProject } from '../../../types/project';
import type { SpecificationDocument } from '../../../types/index';
import { AddNewSpecification } from './AddNewSpecification';
import { UploadSpecificationsDialog } from './UploadSpecificationsDialog';
import { downloadSpecificationTemplate, processSpecificationUpload } from '../../../../../lib/specificationUtils';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { getAllPipeDetails } from '../../../../../redux/Project/setupData/action';
import { deleteSpecificationDoc, deleteSpecifications, getSpecificationsTemplate, CheckPipeDocumentUsingProduction } from '../../../../../redux/Project/specificationSaga';
import { SPEC_TYPE_LABELS } from '../../../constants/specificationConstants';
import { useDispatch, useSelector } from 'react-redux';

interface ProjectSpecificationsProps {
  project: DashboardProject;
  canEdit?: boolean;
  canReport?: boolean;
}

export const ProjectSpecifications: React.FC<ProjectSpecificationsProps> = ({ project, canEdit = true, canReport = true }) => {
  const dispatch = useDispatch();
  const { pipeDetails, isPipeDetailsLoading } = useSelector((state: any) => state.setupData || {});
  const [specifications, setSpecifications] = useState<SpecificationDocument[]>([]);
  const [isAddingSpecification, setIsAddingSpecification] = useState(false);
  const [fatTemplateListVersion] = useState(0);
  const [pipeSizes, setPipeSizes] = useState<any[]>([]);

  // Delete dialog state
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SpecificationDocument[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [productionCheckResponse, setProductionCheckResponse] = useState<any>(null);
  const [isCheckingProduction, setIsCheckingProduction] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [setupData, setSetupData] = useState<any>({});
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [pipeName, setPipeName] = useState('');
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [lastProjectId, setLastProjectId] = useState<string | number | null>(null);

  // Load initial data - only fetch if project ID changed or pipeDetails doesn't match current project
  useEffect(() => {
    const currentProjectId = project.project.projectId;
    
    // Check if project ID changed
    const projectIdChanged = lastProjectId !== null && lastProjectId !== currentProjectId;
    
    // Check if pipeDetails exists and matches current project ID
    const pipeDetailsMatchesProject = pipeDetails && Object.keys(pipeDetails).length > 0 && 
      Object.values(pipeDetails).some((setup: any) => 
        setup.projectId && String(setup.projectId) === String(currentProjectId)
      );

    // Only fetch pipeDetails if:
    // 1. Project ID changed (need to fetch new project's data)
    // 2. pipeDetails is null/undefined/empty
    // 3. pipeDetails exists but doesn't match current project ID
    if (currentProjectId) {
      if (projectIdChanged || !pipeDetails || Object.keys(pipeDetails).length === 0 || !pipeDetailsMatchesProject) {
        dispatch(getAllPipeDetails({ projectId: currentProjectId }));
      }
    }
    
    // Update last project ID
    if (currentProjectId) {
      setLastProjectId(currentProjectId);
    }
  }, [project.project.projectId]);

  // Check if there are any pipes anywhere across all project types
  const hasAnyPipes = () => {
    if (!setupData || typeof setupData !== 'object') return false;
    let hasPipes = false;
    Object.values(setupData).forEach((setup: any) => {
      if (setup && setup.pipeSizes && Array.isArray(setup.pipeSizes) && setup.pipeSizes.length > 0) {
        hasPipes = true;
      }
    });
    return hasPipes;
  };

  // Function to transform API documents to SpecificationDocument format
  const transformDocumentsToSpecifications = (setupData: any): SpecificationDocument[] => {
    const specifications: SpecificationDocument[] = [];


    Object.values(setupData).forEach((setup: any) => {
      if (setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
        setup.pipeSizes.forEach((pipe: any) => {
          if (pipe.documentsData && pipe.documentsData.documents && Array.isArray(pipe.documentsData.documents)) {
            pipe.documentsData.documents.forEach((doc: any) => {
              const specification: SpecificationDocument = {
                id: doc.docRefId?.toString(),
                fileName: doc.fileName || '',
                type: doc.type as SpecificationDocument['type'],
                pipeId: pipe.pipeId?.toString(),
                projectId: setup.projectId,
                projectTypeId: setup.projectTypeId,
                pipeOrder: pipe.pipeOrder || 0,
                fileUrl: doc.fileUrl,
                s3Key: doc.s3Key,
                uploadedBy: doc.uploadedBy,
                uploadDate: doc.uploadDate,
                version: doc.version || 1,
                details: doc.details || {},
                files: doc.files || [],
                docRefId: doc.docRefId || 0
              };
              specifications.push(specification);
            });
          } else {
          }
        });
      }
    });

    return specifications;
  };

  // Function to extract pipe sizes from setup data
  const extractPipeSizes = (setupData: any): any[] => {
    const pipeSizes: any[] = [];


    Object.values(setupData).forEach((setup: any) => {
      if (setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
        setup.pipeSizes.forEach((pipe: any) => {
          const pipeData = {
            pipeId: pipe.pipeId,
            pipeName: pipe.pipeName,
            size: pipe.pipeSize || pipe.size || 0,
            wallThickness: pipe.wallThickness || 0,
            numberOfBuckleArrestors: pipe.numberOfBuckleArrestors || 0,
            numberOfPipes: pipe.numberOfPipes || 0,
            pipeLength: pipe.pipeLength || 0,
            numberOfForges: pipe.numberOfForges || 0,
            outputAssemblyLength: pipe.outputAssemblyLength || 0,
            manufacturerId: pipe.manufacturerId || 0,
            pipeOrder: pipe.pipeOrder || 0,
            isActive: pipe.isActive !== undefined ? pipe.isActive : true,
            documentsData: pipe.documentsData || {
              numberOfDocs: 0,
              isDocumentsAvailable: false,
              documents: []
            },
            materialsData: pipe.materialsData || {
              numberOfMaterials: 0,
              isMaterialsAvailable: false,
              materials: []
            },
            productionData: pipe.productionData || {
              numberOfProduction: 0,
              isProductionAvailable: false,
              production: []
            }
          };
          pipeSizes.push(pipeData);
        });
      }
    });

    return pipeSizes;
  };

  // Update setup data when pipeDetails changes from Redux
  useEffect(() => {
    if (pipeDetails && Object.keys(pipeDetails).length > 0) {
      setSetupData(pipeDetails);

      // Transform documents to specifications format
      const transformedSpecifications = transformDocumentsToSpecifications(pipeDetails);
      setSpecifications(transformedSpecifications);

      // Extract pipe sizes
      const extractedPipeSizes = extractPipeSizes(pipeDetails);
      setPipeSizes(extractedPipeSizes);
    } else if (pipeDetails && Object.keys(pipeDetails).length === 0) {
      setSetupData({});
      setSpecifications([]);
      setPipeSizes([]);
    }
  }, [pipeDetails]);

  // Update loading state based on Redux
  useEffect(() => {
    setIsLoadingData(isPipeDetailsLoading);
  }, [isPipeDetailsLoading]);

  // // Load initial data - use store data if available, only fetch if null/undefined
  // useEffect(() => {
  //   // Use store data if available, don't call API on initial load
  //   // Only fetch if pipeDetails is completely null/undefined (not just empty)
  //   if (pipeDetails === null || pipeDetails === undefined) {
  //     // Dispatch Redux action to fetch pipe details only if store is empty
  //     dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [project.project.projectId]);

  const handleAddSpecification = async (specification: any) => {
    try {
      // After adding new specification, force refresh pipe details from API
      dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      // The useEffect will automatically update when pipeDetails changes
    } catch (error) {
    }
  };

  const handleImport = () => {
    // TODO: Implement import
  };

  const handleExport = () => {
    // TODO: Implement export
  };

  const handleDownload = (spec: any) => {
    if (spec.fileUrl) {
      // Open file URL in a new tab
      window.open(spec.fileUrl, '_blank');
    } else {
    }
  };

  const handleDeleteDocument = async (spec: any) => {
    setItemToDelete(spec);
    setIsBulkDelete(false);
    setProductionCheckResponse(null);
    setIsDeleteModalVisible(true);
    setIsCheckingProduction(true);
    
    // Check if document is used in production
    if (spec.files && spec.files.length > 0 && spec.files[0].docId) {
      const docIds = [spec.files[0].docId];
      try {
        const checkResponse = await CheckPipeDocumentUsingProduction(docIds);
        setProductionCheckResponse(checkResponse);
      } catch (error) {
        setProductionCheckResponse(null);
      }
    }
    setIsCheckingProduction(false);
  };

  const handleDeletePipeSize = async (specs: SpecificationDocument[], pipeName: string) => {
    setPipeName(pipeName);
    setSelectedItems(specs);
    setIsBulkDelete(true);
    setProductionCheckResponse(null);
    setIsDeleteModalVisible(true);
    setIsCheckingProduction(true);
    // Check if any documents are used in production
    const docIds = specs.map(item => item.files?.[0]?.docId)
    if (docIds.length > 0) {
      try {
        const checkResponse = await CheckPipeDocumentUsingProduction(docIds);
        setProductionCheckResponse(checkResponse);
      } catch (error) {
        setProductionCheckResponse(null);
      } 
    }
    setIsCheckingProduction(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setItemToDelete(null);
    setSelectedItems([]);
    setIsBulkDelete(false);
    setProductionCheckResponse(null);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      if (isBulkDelete) {
        // Check if any documents are used in production
        if (productionCheckResponse && productionCheckResponse.usedInProduction && productionCheckResponse.usedInProduction.length > 0) {
          handleCancelDelete();
          return;
        }
        // Handle bulk delete
        const pipeIds = selectedItems.map(item => item.pipeId);
        const deleteResponse = await deleteSpecifications(pipeIds, pipeName);

        if (deleteResponse) {
          handleCancelDelete();
          // Refresh data from API after bulk delete
          await refreshSpecificationsData();
          // toast.success(`Successfully deleted ${selectedItems.length} specifications`);
        }
      } else if (itemToDelete && itemToDelete.files && itemToDelete.files.length > 0) {
        // Check if document is used in production
        if (productionCheckResponse && productionCheckResponse.usedInProduction && productionCheckResponse.usedInProduction.length > 0) {
          handleCancelDelete();
          return;
        }
        // Handle specification deletion only
        const docIds = [itemToDelete.files[0].docId];
        const deleteResponse = await deleteSpecificationDoc(docIds,
          itemToDelete, SPEC_TYPE_LABELS[itemToDelete.type as keyof typeof SPEC_TYPE_LABELS]);

        if (deleteResponse) {
          handleCancelDelete();
          // Refresh data from API after individual delete
          await refreshSpecificationsData();
        }
      }
    } catch (error) {
    } finally {
      setDeleteLoading(false);
      handleCancelDelete();
    }
  };

  // Function to refresh specifications data from API after delete
  const refreshSpecificationsData = async (spec: SpecificationDocument | null = null) => {
    try {
      // After delete, force refresh pipe details from API
      dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      // The useEffect will automatically update when pipeDetails changes
    } catch (error) {
    }
  };

  const groupedSpecifications = useMemo(() => {
    const grouped = specifications.reduce((acc, spec) => {
      const pipeId = spec.pipeId;
      if (!acc[pipeId]) {
        acc[pipeId] = [];
      }
      acc[pipeId].push(spec);
      return acc;
    }, {} as Record<string, SpecificationDocument[]>);

    // Sort specifications within each pipe group by type and date
    Object.keys(grouped).forEach(pipeId => {
      grouped[pipeId].sort((a, b) => {
        if (a.type !== b.type) {
          return a.type.localeCompare(b.type);
        }
        return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
      });
    });

    return grouped;
  }, [specifications]);




  const handleDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      await getSpecificationsTemplate(project.project.projectId, project.project.projectTypes.pipeline?.project_type_id, "PIPE_DETAILS");
      setIsDownloadingTemplate(false)
    } catch (error) {
      //  toast.error('Failed to download template');
      setIsDownloadingTemplate(false);
    }
  };

  const handleUploadComplete = async () => {
    try {
      // Refresh data from Redux store - dispatch to fetch fresh data
      dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      // The useEffect will automatically update when pipeDetails changes
    } catch (error) {
    }
  };

  return (
    <div>
      {isLoadingData ? (
        <div className="flex items-center justify-center min-h-[700px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
            <span className="text-gray-600 text-lg">Loading specifications data...</span>
          </div>
        </div>
      ) : (
        <>
          <Card className="border-none shadow-none">
            <CardContent className="px-6 pt-2 pb-6">
              <>
                <AllProjectSpecifications
                  project={project}
                  groupedSpecifications={groupedSpecifications}
                  handleRefresh={refreshSpecificationsData}
                  handleDeleteDocument={handleDeleteDocument}
                  handleDeletePipeSize={handleDeletePipeSize}
                  setupData={setupData}
                  canEdit={canEdit}
                  canReport={canReport}
                  fatTemplateListVersion={fatTemplateListVersion}
                  toolbar={
                    <>
                      {canEdit && (
                        <Button
                          disabled={!hasAnyPipes()}
                          onClick={() => setIsAddingSpecification(true)}
                          size="sm"
                          className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
                        >
                          <Plus className="h-4 w-4" />
                          Add Specification
                        </Button>
                      )}
                      {(canEdit || canReport) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors">
                            <MoreHorizontal className="h-4 w-4 text-text-grey" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEdit && (
                              <DropdownMenuItem onClick={handleDownloadTemplate}>
                                <Download className="h-4 w-4 mr-2" />
                                Download Template
                              </DropdownMenuItem>
                            )}
                            {canEdit && (
                              <DropdownMenuItem onClick={() => setIsUploadDialogOpen(true)}>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Specifications
                              </DropdownMenuItem>
                            )}
                            {canReport && (
                              <DropdownMenuItem onClick={handleExport}>
                                <FileText className="h-4 w-4 mr-2" />
                                Export Report
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </>
                  }
                />

                {isAddingSpecification && <AddNewSpecification
                  isOpen={isAddingSpecification}
                  onClose={() => setIsAddingSpecification(false)}
                  onAdd={handleAddSpecification}
                  project={project}
                  setupData={setupData}
                />}

                {isUploadDialogOpen && <UploadSpecificationsDialog
                  isOpen={isUploadDialogOpen}
                  onClose={() => setIsUploadDialogOpen(false)}
                  onUploadComplete={handleUploadComplete}
                  setupData={setupData}
                />}
              </>
            </CardContent>
          </Card>

          {/* Delete Confirmation Dialog */}
          <Dialog open={isDeleteModalVisible} onOpenChange={setIsDeleteModalVisible}>
            <DialogContent className="max-w-[800px] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <DialogTitle className="text-xl font-semibold">
                  {isBulkDelete ? 'Delete Specifications' : 'Delete Specification'}
                </DialogTitle>
              </div>


              {(() => {
                // Show loading state while checking production
                if (isCheckingProduction) {
                  return (
                    <div className="flex items-center justify-center py-8">
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-gray-600">Checking production usage...</span>
                      </div>
                    </div>
                  );
                }

                // Check production usage from API response
                const documentStatuses = productionCheckResponse?.documentStatuses || [];
                const usedDocuments = documentStatuses.filter((status: any) => status.isUsedInProduction);
                const hasProduction = usedDocuments.length > 0;
                
                if (isBulkDelete) {
                  if (hasProduction) {
                    // Show warning - cannot delete because used in production
                    return (
                      <div>
                        <div className="mb-3">
                          <p className="text-gray-700 mb-3">
                            ΓÜá∩╕Å Cannot delete specifications <span className="font-semibold text-red-600"> {pipeName} </span> because some specifications have been used in production.
                          </p>
                        </div>
                        <div className="mt-4 p-3 bg-yellow-50 border rounded-md">
                          <p className="text-sm text-yellow-700 mb-2">
                            <span className="font-semibold">Documents used in production:</span>
                          </p>
                          <div className="max-h-32 overflow-y-auto">
                            {usedDocuments.map((doc: any, index: number) => {
                              const specTypeLabel = SPEC_TYPE_LABELS[doc.specification as keyof typeof SPEC_TYPE_LABELS] || doc.specification;
                              return (
                                <p key={index} className="text-sm text-yellow-700">
                                  ΓÇó {specTypeLabel} (File Name: {selectedItems.find(item => item.files?.[0]?.docId === doc.docId)?.files?.[0]?.fileName || selectedItems.find(item => item.files?.[0]?.docId === doc.docId)?.fileName})
                                </p>
                              );
                            })}
                          </div>
                          <p className="text-sm text-yellow-700 mt-2">
                            <span className="font-semibold">Action Required:</span>
                            <br />
                            <span className="block mt-1">
                              ΓÇó Please remove all associated production data before deleting these specifications.
                            </span>
                          </p>
                        </div>
                      </div>
                    );
                  }
                } else {
                  if (hasProduction) {
                    // Show warning - cannot delete because used in production
                    const usedDoc = usedDocuments[0];
                    const specTypeLabel = usedDoc ? (SPEC_TYPE_LABELS[usedDoc.specification as keyof typeof SPEC_TYPE_LABELS] || usedDoc.specification) : '';
                    return (
                      <div>
                        <div className="mb-3">
                          <p className="text-gray-700 mb-3">
                            ΓÜá∩╕Å Cannot delete specification <span className="font-semibold text-red-600"> {itemToDelete?.files[0]?.fileName || itemToDelete?.fileName} </span> because it has been used in production.
                          </p>
                        </div>
                        <div className="mt-4 p-3 bg-yellow-50 border rounded-md">
                          <p className="text-sm text-yellow-700">
                            <span className="font-semibold">Action Required:</span>
                            <br />
                            <span className="block mt-1">
                              ΓÇó Please remove all associated production data before deleting this specification.
                            </span>
                            {usedDoc && (
                              <span className="block mt-1">
                                ΓÇó Specification Type: <span className="font-semibold">{specTypeLabel}</span> (File Name: {itemToDelete?.files[0]?.fileName || itemToDelete?.fileName})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  }
                }
                 

                // If no blocking data, show delete confirmation
                return (
                  <div className="mb-6">
                    {isBulkDelete ? (
                      <>
                        <p className="text-gray-700 mb-3">
                          Are you sure you want to <span className="font-semibold text-red-600">delete</span> the
                          specification documents from <span className="font-semibold text-red-600"> {pipeName} </span> ?
                        </p>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-sm text-gray-600 mb-2">Document items:</p>
                          <div className="max-h-32 overflow-y-auto">
                            {selectedItems.map((item, index) => (
                              <div key={item.id} className="text-sm text-gray-700 py-1">
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <p className="text-sm text-gray-600 mb-1">{SPEC_TYPE_LABELS[item.type as keyof typeof SPEC_TYPE_LABELS]}:</p>
                                  <p className="text-sm text-gray-700">ΓÇó File Name: {item?.files?.[0]?.fileName || item.fileName || 'N/A'}</p>
                                  <p className="text-sm text-gray-700">ΓÇó Upload Date: {new Date(item?.files?.[0]?.uploadDate || item?.uploadDate).toLocaleDateString()}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-700 mb-3">
                          Are you sure you want to <span className="font-semibold text-red-600">delete</span> the specification document
                          <span className="font-semibold text-red-600"> "{itemToDelete?.files?.[0]?.fileName || itemToDelete?.fileName || SPEC_TYPE_LABELS[itemToDelete?.type as keyof typeof SPEC_TYPE_LABELS]}"</span>?
                        </p>
                        {itemToDelete && (
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-sm text-gray-600 mb-1">Specification details:</p>
                            <p className="text-sm text-gray-700">ΓÇó Type: {SPEC_TYPE_LABELS[itemToDelete.type as keyof typeof SPEC_TYPE_LABELS]}</p>
                            <p className="text-sm text-gray-700">ΓÇó File Name: {itemToDelete?.files?.[0]?.fileName || itemToDelete?.fileName || 'N/A'}</p>
                            <p className="text-sm text-gray-700">ΓÇó Upload Date: {new Date(itemToDelete?.files?.[0]?.uploadDate || itemToDelete?.uploadDate).toLocaleDateString()}</p>
                          </div>
                        )}
                      </>
                    )}

                    <div className="mt-4 p-3 bg-red-50 border rounded-md">
                      <p className="text-sm text-red-700">
                        <span className="font-semibold">Warning:</span> This action is irreversible.
                        {isBulkDelete
                          ? ' All specifications documents will be permanently removed from the system.'
                          : ' This specification document will be permanently removed from the system.'
                        }
                      </p>
                    </div>
                  </div>
                );
              })()}

              <DialogFooter className="gap-3">
                <Button
                  variant="outline"
                  onClick={handleCancelDelete}
                  disabled={deleteLoading}
                  className="min-w-[100px]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  disabled={(() => {
                    if (deleteLoading || isCheckingProduction) return true;
                    const documentStatuses = productionCheckResponse?.documentStatuses || [];
                    const usedDocuments = documentStatuses.filter((status: any) => status.isUsedInProduction);
                    return usedDocuments.length > 0;
                  })()}
                  className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
                >
                  {deleteLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {isBulkDelete ? 'Deleting...' : 'Deleting...'}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4" />
                      {isBulkDelete ? 'Delete All' : 'Delete'}
                    </div>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}; 
